import type { World } from '@/ecs/World';
import type { Entity } from '@/ecs/Entity';
import type { EventBus } from '@/core/EventBus';
import type { UnitFactory } from '@/units/UnitFactory';
import type { BenchManager } from '@/placement/BenchManager';
import type { CurrencyManager } from '@/economy/CurrencyManager';
import type { UnitPool } from '@/shop/UnitPool';
import { UnitData, Owner } from '@/ecs/components/UnitComponents';
import { UNIT_DEFINITIONS, type UnitDefinition } from '@/units/UnitDefinitions';

/**
 * Shop events
 */
export const ShopEvents = {
  SHOP_REFRESHED: 'shop:refreshed',
  UNIT_BOUGHT: 'shop:unitBought',
  UNIT_SOLD: 'shop:unitSold',
  SHOP_LOCKED: 'shop:locked',
  SHOP_UNLOCKED: 'shop:unlocked',
  SLOT_SELECTED: 'shop:slotSelected',
  SLOT_DESELECTED: 'shop:slotDeselected',
} as const;

export interface ShopSlot {
  unitType: string | null;
  cost: number;
  purchased: boolean;
}

/**
 * Shop configuration
 */
export interface ShopConfig {
  slots: number;
  tierOdds: number[][]; // [playerLevel][tier] = chance
  tierCosts: number[]; // Cost per tier
}

export const DEFAULT_SHOP_CONFIG: ShopConfig = {
  slots: 3,
  // Odds for each tier at different "player levels"
  // Since there's no leveling, use a fixed distribution
  tierOdds: [
    [0.6, 0.3, 0.1, 0, 0], // Default distribution
  ],
  tierCosts: [1, 2, 3, 4, 5], // Tier 1 = 1 gold, etc.
};

/**
 * Shop system for buying and selling units
 */
export class ShopSystem {
  private eventBus: EventBus;
  private unitFactory: UnitFactory;
  private benchManager: BenchManager;
  private currencyManager: CurrencyManager;
  private unitPool: UnitPool;
  private config: ShopConfig;

  // Shop state per player
  private player1Shop: ShopSlot[];
  private player2Shop: ShopSlot[];
  private player1Locked = false;
  private player2Locked = false;

  // Track reserved units (shown in shop but not yet bought)
  // Map<playerId, unitTypes[]>
  private reservedUnits: Map<number, string[]> = new Map();

  constructor(
    _world: World,
    eventBus: EventBus,
    unitFactory: UnitFactory,
    benchManager: BenchManager,
    currencyManager: CurrencyManager,
    unitPool: UnitPool,
    config: ShopConfig = DEFAULT_SHOP_CONFIG
  ) {
    this.eventBus = eventBus;
    this.unitFactory = unitFactory;
    this.benchManager = benchManager;
    this.currencyManager = currencyManager;
    this.unitPool = unitPool;
    this.config = config;

    this.player1Shop = this.createEmptyShop();
    this.player2Shop = this.createEmptyShop();
    this.reservedUnits.set(1, []);
    this.reservedUnits.set(2, []);
  }

  /**
   * Create empty shop slots
   */
  private createEmptyShop(): ShopSlot[] {
    return new Array(this.config.slots).fill(null).map(() => ({
      unitType: null,
      cost: 0,
      purchased: false,
    }));
  }

  /**
   * Get shop for a player
   */
  private getShop(playerId: number): ShopSlot[] {
    return playerId === 1 ? this.player1Shop : this.player2Shop;
  }

  /**
   * Check if shop is locked
   */
  isLocked(playerId: number): boolean {
    return playerId === 1 ? this.player1Locked : this.player2Locked;
  }

  /**
   * Toggle shop lock
   */
  toggleLock(playerId: number): boolean {
    if (playerId === 1) {
      this.player1Locked = !this.player1Locked;
      this.eventBus.emit(
        this.player1Locked ? ShopEvents.SHOP_LOCKED : ShopEvents.SHOP_UNLOCKED,
        { playerId }
      );
      return this.player1Locked;
    } else {
      this.player2Locked = !this.player2Locked;
      this.eventBus.emit(
        this.player2Locked ? ShopEvents.SHOP_LOCKED : ShopEvents.SHOP_UNLOCKED,
        { playerId }
      );
      return this.player2Locked;
    }
  }

  /**
   * Refresh shop with new units
   */
  refreshShop(playerId: number, free: boolean = false): boolean {
    // Check if locked
    if (this.isLocked(playerId) && !free) {
      return false;
    }

    // Pay for reroll if not free
    if (!free) {
      const cost = this.currencyManager.getRerollCost();
      if (!this.currencyManager.spendGold(playerId, cost, 'Shop reroll')) {
        return false;
      }
    }

    // Return previously reserved (unbought) units to the pool
    this.returnReservedUnits(playerId);

    const shop = this.getShop(playerId);
    const newReserved: string[] = [];

    // Generate new units for each slot
    for (let i = 0; i < shop.length; i++) {
      const tier = this.rollTier();
      const unit = this.rollUnitFromTier(tier);

      if (unit && this.unitPool.take(unit.unitType)) {
        // Successfully took from pool
        shop[i] = {
          unitType: unit.unitType,
          cost: this.config.tierCosts[tier - 1] ?? 1,
          purchased: false,
        };
        newReserved.push(unit.unitType);
      } else {
        // No unit available, empty slot
        shop[i] = {
          unitType: null,
          cost: 0,
          purchased: false,
        };
      }
    }

    // Track the new reserved units
    this.reservedUnits.set(playerId, newReserved);

    this.eventBus.emit(ShopEvents.SHOP_REFRESHED, {
      playerId,
      shop: [...shop],
    });

    return true;
  }

  /**
   * Return reserved (unbought) units to the pool
   */
  private returnReservedUnits(playerId: number): void {
    const reserved = this.reservedUnits.get(playerId) ?? [];
    for (const unitType of reserved) {
      this.unitPool.return(unitType, 1); // Reserved units are always 1-star
    }
    this.reservedUnits.set(playerId, []);
  }

  /**
   * Roll a tier based on odds
   */
  private rollTier(): number {
    const odds = this.config.tierOdds[0]; // Use fixed odds
    const roll = Math.random();
    let cumulative = 0;

    for (let tier = 0; tier < odds.length; tier++) {
      cumulative += odds[tier];
      if (roll < cumulative) {
        return tier + 1;
      }
    }

    return 1; // Default to tier 1
  }

  /**
   * Roll a random unit from a tier (only units with pool availability)
   */
  private rollUnitFromTier(tier: number): UnitDefinition | null {
    // Get available units from the pool for this tier
    let availableUnits = this.unitPool.getAvailableUnitsOfTier(tier);

    // If no units available in this tier, try adjacent tiers
    if (availableUnits.length === 0) {
      // Try tier below first, then tier above
      for (const fallbackTier of [tier - 1, tier + 1, tier - 2, tier + 2]) {
        if (fallbackTier >= 1 && fallbackTier <= 5) {
          availableUnits = this.unitPool.getAvailableUnitsOfTier(fallbackTier);
          if (availableUnits.length > 0) break;
        }
      }
    }

    // If still no units available, return null (empty slot)
    if (availableUnits.length === 0) {
      return null;
    }

    return availableUnits[Math.floor(Math.random() * availableUnits.length)];
  }

  /**
   * Get shop state for a player
   */
  getShopState(playerId: number): ShopSlot[] {
    return [...this.getShop(playerId)];
  }

  /**
   * Purchase a shop slot without creating a unit (for direct board placement)
   * Returns the unit type if successful, null otherwise
   */
  purchaseSlot(playerId: number, slotIndex: number): string | null {
    const shop = this.getShop(playerId);

    if (slotIndex < 0 || slotIndex >= shop.length) {
      return null;
    }

    const slot = shop[slotIndex];
    if (!slot.unitType || slot.purchased) {
      return null;
    }

    // Try to purchase
    if (!this.currencyManager.spendGold(playerId, slot.cost, `Buy ${slot.unitType}`)) {
      return null;
    }

    // Remove from reserved units (already taken from pool at refresh time)
    const reserved = this.reservedUnits.get(playerId) ?? [];
    const reservedIndex = reserved.indexOf(slot.unitType);
    if (reservedIndex !== -1) {
      reserved.splice(reservedIndex, 1);
    }

    const unitType = slot.unitType;

    // Mark slot as purchased
    slot.purchased = true;

    this.eventBus.emit(ShopEvents.UNIT_BOUGHT, {
      playerId,
      slotIndex,
      unitType,
      entityId: null, // No entity created yet
      cost: slot.cost,
    });

    return unitType;
  }

  /**
   * Buy a unit from shop slot (creates unit on bench)
   */
  buyUnit(playerId: number, slotIndex: number): Entity | null {
    const shop = this.getShop(playerId);

    if (slotIndex < 0 || slotIndex >= shop.length) {
      return null;
    }

    const slot = shop[slotIndex];
    if (!slot.unitType || slot.purchased) {
      return null;
    }

    // Check if bench is full
    if (this.benchManager.isBenchFull(playerId)) {
      return null;
    }

    // Try to purchase
    if (!this.currencyManager.spendGold(playerId, slot.cost, `Buy ${slot.unitType}`)) {
      return null;
    }

    // Create unit on bench
    const benchSlot = this.benchManager.getFirstEmptySlot(playerId);
    const entity = this.unitFactory.createBenchUnit(slot.unitType, playerId, benchSlot);

    if (!entity) {
      // Refund if creation failed
      this.currencyManager.addGold(playerId, slot.cost, 'Refund - unit creation failed');
      return null;
    }

    // Add to bench manager's tracking
    this.benchManager.addToBench(entity, playerId);

    // Remove from reserved units (already taken from pool at refresh time)
    const reserved = this.reservedUnits.get(playerId) ?? [];
    const reservedIndex = reserved.indexOf(slot.unitType);
    if (reservedIndex !== -1) {
      reserved.splice(reservedIndex, 1);
    }

    // Mark slot as purchased
    slot.purchased = true;

    this.eventBus.emit(ShopEvents.UNIT_BOUGHT, {
      playerId,
      slotIndex,
      unitType: slot.unitType,
      entityId: entity.id,
      cost: slot.cost,
    });

    return entity;
  }

  /**
   * Sell a unit for gold
   */
  sellUnit(entity: Entity, playerId: number): number {
    const unitDataComponent = entity.getComponent(UnitData);
    if (!unitDataComponent) return 0;

    const definition = UNIT_DEFINITIONS[unitDataComponent.unitType];
    if (!definition) return 0;

    // Calculate sell value based on tier and star level
    const baseCost = this.config.tierCosts[definition.tier - 1] ?? 1;
    const starMultiplier = Math.pow(2, unitDataComponent.starLevel - 1); // 1★=1x, 2★=2x, 3★=4x, 4★=8x
    const sellValue = Math.floor(baseCost * starMultiplier * 0.5);

    // Give gold
    this.currencyManager.addGold(playerId, sellValue, `Sell ${unitDataComponent.name}`);

    // Return copies to the pool
    this.unitPool.return(unitDataComponent.unitType, unitDataComponent.starLevel);

    // Remove unit
    const owner = entity.getComponent(Owner);
    if (owner?.isOnBench) {
      this.benchManager.removeFromBench(entity.id, playerId);
    }
    this.unitFactory.removeUnit(entity);

    this.eventBus.emit(ShopEvents.UNIT_SOLD, {
      playerId,
      entityId: entity.id,
      unitType: unitDataComponent.unitType,
      sellValue,
    });

    return sellValue;
  }

  /**
   * Initialize shops at start of round
   */
  initializeRound(): void {
    // Auto-refresh shops if not locked
    if (!this.player1Locked) {
      this.refreshShop(1, true);
    }
    if (!this.player2Locked) {
      this.refreshShop(2, true);
    }
  }

  /**
   * Reset shops for new match
   */
  reset(): void {
    // Return any reserved units before clearing
    this.returnReservedUnits(1);
    this.returnReservedUnits(2);

    this.player1Shop = this.createEmptyShop();
    this.player2Shop = this.createEmptyShop();
    this.player1Locked = false;
    this.player2Locked = false;
  }

  /**
   * Sync shop from server (for multiplayer)
   */
  syncFromServer(playerId: number, slots: Array<{ unitType: string; tier: number; cost: number }>): void {
    const shop = this.getShop(playerId);

    for (let i = 0; i < this.config.slots; i++) {
      if (i < slots.length && slots[i].unitType) {
        shop[i] = {
          unitType: slots[i].unitType,
          cost: slots[i].cost,
          purchased: false,
        };
      } else {
        shop[i] = {
          unitType: null,
          cost: 0,
          purchased: false,
        };
      }
    }

    this.eventBus.emit(ShopEvents.SHOP_REFRESHED, {
      playerId,
      shop: [...shop],
    });
  }
}
